<div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

  <!-- State tabs (each container's own state) -->
  <div class="px-3 sm:px-5 border-b border-gray-100 overflow-x-auto">
    <div class="flex items-center gap-1 min-w-max">
      @for (tab of tabs; track tab) {
      <button class="relative flex items-center gap-2 px-3 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap"
        [ngClass]="status === tab ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'" (click)="setTab(tab)">
        @if (tab !== 'ALL') { <span class="h-2 w-2 rounded-full" [ngClass]="statusDot(tab)"></span> }
        <span class="capitalize">{{ tab === 'ALL' ? 'All lines' : (tab | lowercase) }}</span>
        <span class="rounded-full px-1.5 min-w-[22px] text-center text-[11px] po-num-tab" [ngClass]="status === tab ? 'bg-neutral-900 text-amber-400' : 'bg-gray-100 text-gray-500'">{{ tabCount(tab) }}</span>
        @if (status === tab) { <span class="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-amber-400"></span> }
      </button>
      }
    </div>
  </div>

  <!-- Search -->
  <div class="px-4 sm:px-5 pt-4 pb-3">
    <div class="relative">
      <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
      <input type="text" placeholder="Search by PO, product / SKU, container, BL, invoice no., vessel or shipping line"
        class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
        [(ngModel)]="search" (ngModelChange)="onSearch()" (keydown.escape)="clearSearch()" />
      @if (search) {
      <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
      </button>
      }
    </div>
  </div>

  <!-- Spreadsheet: export / import (the table's columns) -->
  <div class="px-4 sm:px-5 pb-4 flex items-center gap-3 flex-wrap border-b border-gray-100">
    <input #importInput type="file" class="hidden" accept=".xlsx,.xls,.csv" (change)="onImportFile($event)" />
    <span class="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 mr-1 hidden sm:inline">Spreadsheet</span>
    <button type="button"
      class="group inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
      [disabled]="exporting" (click)="exportSheet()" matTooltip="Download every line matching your search and tab - same columns as the table">
      <span class="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center shadow-sm shadow-emerald-200 group-hover:scale-105 transition-transform">
        @if (exporting) { <mat-spinner diameter="14" class="export-spinner"></mat-spinner> } @else { <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">download</mat-icon> }
      </span>
      <span class="text-left leading-tight">
        <span class="block text-sm font-bold text-gray-900">Export</span>
        <span class="block text-[10px] font-medium text-gray-400 po-num-tab">{{ count }} line{{ count === 1 ? '' : 's' }} &middot; .xlsx</span>
      </span>
    </button>
    <button type="button"
      class="group inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-sky-300 hover:shadow-md hover:shadow-sky-100 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
      [disabled]="importing" (click)="importInput.click()" matTooltip="Upload an edited export to update lines, containers and shipments - you'll see every change first">
      <span class="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-500 text-white flex items-center justify-center shadow-sm shadow-sky-200 group-hover:scale-105 transition-transform">
        @if (importing) { <mat-spinner diameter="14" class="export-spinner"></mat-spinner> } @else { <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">upload</mat-icon> }
      </span>
      <span class="text-left leading-tight">
        <span class="block text-sm font-bold text-gray-900">Import</span>
        <span class="block text-[10px] font-medium text-gray-400">Update from a sheet</span>
      </span>
    </button>
    <span class="ml-auto hidden lg:flex items-center gap-1.5 text-[11px] text-gray-400">
      <mat-icon class="!text-[14px] !h-3.5 !w-3.5">info</mat-icon>
      Edit the cells in Excel and import it back - you'll see every change before it's saved
    </span>
  </div>

  @if (loading && containers.length === 0) {
  <div class="p-6 flex flex-col gap-3 animate-pulse">
    @for (r of skeletonRows; track r) { <div class="h-9 rounded-lg bg-gray-100"></div> }
  </div>
  } @else if (containers.length === 0) {
  <div class="min-h-64 flex flex-col justify-center items-center text-center px-6 py-14">
    <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">{{ search || status !== 'ALL' ? 'search_off' : 'view_in_ar' }}</mat-icon></span>
    <div class="text-lg font-semibold text-gray-800">{{ search || status !== 'ALL' ? 'Nothing matches' : 'No container lines yet' }}</div>
    <div class="text-sm text-gray-500 mt-1 max-w-sm">{{ search || status !== 'ALL' ? 'Try a different search or state.' : 'Lines appear here once they are added to a shipment.' }}</div>
  </div>
  } @else {

  <!-- One row per line in a container. Header sticks on scroll; PO + Product stay pinned left. -->
  <div class="ctr-table-wrap overflow-auto max-h-[70vh] transition-opacity" [class.opacity-50]="loading">
    <table class="ctr-table w-full text-sm">
      <thead>
        <tr>
          <th class="sticky-col col-check">
            <button type="button" class="sh-check" [class.on]="allOnPageSelected" [class.mixed]="someOnPageSelected" (click)="toggleSelectPage()"
              [attr.aria-pressed]="allOnPageSelected" [attr.aria-label]="allOnPageSelected ? 'Unselect this page' : 'Select this page'">
              <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ allOnPageSelected ? 'check' : 'remove' }}</mat-icon>
            </button>
          </th>
          <th class="sticky-col col-po">PO</th>
          <th class="sticky-col col-product">Product</th>
          <th>Incoterm</th>
          <th class="text-right">Quantity</th>
          <th class="text-right">Unit price</th>
          <th class="text-right">Subtotal</th>
          <th>Invoice date</th>
          <th>Invoice no.</th>
          <th>Container</th>
          <th>BL</th>
          <th>ETD</th>
          <th>ETA</th>
          <th>POD</th>
          <th>POL</th>
          <th>Vessel</th>
          <th>Shipping line</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        @for (c of containers; track c.id) {
        <tr class="group cursor-pointer" [class.is-selected]="isSelected(c)" tabindex="0" (click)="open(c)" (keydown.enter)="open(c)" [attr.aria-label]="'Open ' + (c.containerNo || c.shipmentReference)">
          <td class="sticky-col col-check" (click)="$event.stopPropagation()">
            <span class="ctr-accent" [ngClass]="statusDot(c.status)"></span>
            <button type="button" class="sh-check" [class.on]="isSelected(c)" (click)="toggleSelect(c, $event)" (keydown.enter)="$event.stopPropagation()"
              [attr.aria-pressed]="isSelected(c)" [attr.aria-label]="(isSelected(c) ? 'Unselect ' : 'Select ') + (c.poReference || '') + ' ' + (c.containerNo || '')">
              <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ isSelected(c) ? 'check' : 'remove' }}</mat-icon>
            </button>
          </td>
          <td class="sticky-col col-po">
            @if (c.poReference) {
            <a class="font-semibold text-gray-900 hover:text-amber-700 hover:underline" [routerLink]="['/purchases/edit', c.purchaseOrderId]" (click)="$event.stopPropagation()">{{ c.poReference }}</a>
            } @else { <span class="text-gray-300">-</span> }
            <a class="block text-[11px] text-gray-400 hover:text-amber-700" [routerLink]="['/shipments/edit', c.shipmentId]" (click)="$event.stopPropagation()">{{ c.shipmentReference }}</a>
          </td>
          <td class="sticky-col col-product">
            <div class="font-medium text-gray-900 truncate max-w-[260px]" [matTooltip]="c.productName">{{ c.productName }}</div>
            @if (c.sku) {
            <a class="block font-mono text-[11px] text-gray-400 truncate max-w-[260px] hover:text-amber-700 hover:underline" [routerLink]="['/inventory']" [queryParams]="{ q: c.sku }" (click)="$event.stopPropagation()">{{ c.sku }}</a>
            }
          </td>
          <td>@if (c.incoterm) { <span class="text-[11px] font-bold px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700">{{ c.incoterm }}</span> } @else { <span class="text-gray-300">-</span> }</td>
          <td class="text-right whitespace-nowrap po-num-tab"><b class="text-gray-900">{{ c.quantity | number: '1.0-2' }}</b></td>
          <td class="text-right whitespace-nowrap po-num-tab text-gray-700">{{ currencySymbol(c.currency) }}{{ c.unitPrice | number: '1.2-2' }}</td>
          <td class="text-right whitespace-nowrap po-num-tab font-bold text-gray-900">{{ currencySymbol(c.currency) }}{{ c.subtotal | number: '1.2-2' }}</td>
          <td class="whitespace-nowrap text-gray-700">{{ c.invoiceDate ? (c.invoiceDate | date: 'dd/MM/yyyy') : '-' }}</td>
          <td class="whitespace-nowrap font-mono text-[12px] text-gray-700">{{ c.invoiceNumber || '-' }}</td>
          <td class="whitespace-nowrap">
            <span class="font-mono font-bold text-gray-900">{{ c.containerNo || '-' }}</span>
            @if (c.clearedAt) { <div class="text-[10px] font-semibold text-violet-700">cleared {{ c.clearedAt | date: 'd MMM' }}</div> }
          </td>
          <td class="whitespace-nowrap font-mono text-[12px] text-gray-700">{{ c.billOfLading || '-' }}</td>
          <td class="whitespace-nowrap text-gray-700">{{ c.etd ? (c.etd | date: 'dd/MM/yyyy') : '-' }}</td>
          <td class="whitespace-nowrap text-gray-700">
            {{ c.eta ? (c.eta | date: 'dd/MM/yyyy') : '-' }}
            @if (daysToEta(c); as d) {
              @if (d < 0) { <div class="text-[10px] font-semibold text-red-700">{{ -d }}d late</div> }
              @else if (d <= 7) { <div class="text-[10px] font-semibold text-emerald-700">in {{ d }} day{{ d === 1 ? '' : 's' }}</div> }
            }
          </td>
          <td class="whitespace-nowrap text-gray-700">{{ c.portOfDischarge || '-' }}</td>
          <td class="whitespace-nowrap text-gray-700">{{ c.portOfLoading || '-' }}</td>
          <td class="whitespace-nowrap text-gray-700">{{ c.vesselDetails || '-' }}</td>
          <td class="whitespace-nowrap text-gray-700">{{ c.shippingLine || '-' }}</td>
          <td class="whitespace-nowrap" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()">
            <div class="flex items-center gap-1">
              @if (c.status === 'RECEIVED' || c.status === 'CANCELLED' || !c.containerId) {
              <span class="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-0.5 capitalize" [ngClass]="getStatusClass(c.status)">
                @if (c.status === 'RECEIVED') { <mat-icon class="!text-[12px] !h-3 !w-3">lock</mat-icon> }{{ c.status | lowercase }}
              </span>
              } @else {
              @if (c.status !== 'DELIVERED') {
              <button type="button" class="ctr-mic h-7 w-7 rounded-full flex items-center justify-center transition-all flex-shrink-0"
                [ngClass]="listeningId === c.id ? 'bg-red-500 text-white is-listening' : 'bg-violet-50 text-violet-600 hover:bg-violet-100'"
                [disabled]="savingId === c.id" (click)="speak(c)" [matTooltip]="listeningId === c.id ? 'Listening - say a state, e.g. “cleared”' : 'Say the container\'s new state'" aria-label="Say the container's new state">
                <mat-icon class="!text-[15px] !h-[15px] !w-[15px] relative z-10">{{ listeningId === c.id ? 'graphic_eq' : 'mic' }}</mat-icon>
              </button>
              }
              <button type="button" class="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full pl-2.5 pr-1 py-0.5 capitalize hover:shadow-sm transition-shadow"
                [ngClass]="getStatusClass(c.status)" [matMenuTriggerFor]="stateMenu" [disabled]="savingId === c.id" [matTooltip]="'Change ' + (c.containerNo || 'container') + '\'s state'">
                {{ c.status | lowercase }}<mat-icon class="!text-[14px] !h-3.5 !w-3.5 opacity-60">expand_more</mat-icon>
              </button>
              <mat-menu #stateMenu="matMenu" xPosition="before">
                @for (st of stateOptions; track st.value) {
                <button mat-menu-item (click)="setState(c, st.value)" [disabled]="st.value === c.status">
                  <span class="inline-flex items-center gap-2.5"><span class="h-2 w-2 rounded-full" [ngClass]="statusDot(st.value)"></span>{{ st.label }}</span>
                </button>
                }
              </mat-menu>
              }
            </div>
          </td>
        </tr>
        }
      </tbody>
    </table>
  </div>

  <!-- Totals of everything matching (all pages) -->
  <div class="flex items-center gap-x-5 gap-y-1 flex-wrap px-5 py-3 border-t border-gray-100 bg-gray-50/60 text-xs text-gray-500 po-num-tab">
    <span><b class="text-gray-900">{{ count }}</b> line{{ count === 1 ? '' : 's' }}</span>
    <span><b class="text-gray-900">{{ units | number: '1.0-2' }}</b> units</span>
    @for (t of totals; track t.currency) {
    <span>Subtotal <b class="text-gray-900 text-sm">{{ currencySymbol(t.currency) }}{{ t.total | number: '1.2-2' }}</b> {{ t.currency }}</span>
    }
  </div>

  <mat-paginator [length]="count" [pageSize]="pageSize" [pageIndex]="pageIndex" [pageSizeOptions]="[20, 50, 100]" showFirstLastButtons (page)="onPage($event)"></mat-paginator>
  }
</div>

<!-- Bulk actions for the selected lines -->
@if (selected.size) {
<div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]">
  <div class="flex items-center gap-1 rounded-2xl bg-neutral-900/95 backdrop-blur text-white pl-4 pr-2 py-2 shadow-2xl shadow-black/30 ring-1 ring-white/10">
    <span class="flex items-center gap-2 pr-3 mr-1 border-r border-white/10 whitespace-nowrap">
      <span class="h-6 min-w-[24px] px-1.5 rounded-full bg-amber-400 text-neutral-900 text-xs font-bold flex items-center justify-center po-num-tab">{{ selected.size }}</span>
      <span class="text-sm font-semibold">line{{ selected.size === 1 ? '' : 's' }} selected</span>
    </span>
    <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-50" [disabled]="bulkBusy" (click)="exportSelected()">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-emerald-400">download</mat-icon><span class="hidden sm:inline">Export</span>
    </button>
    <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-50" [disabled]="bulkBusy" [matMenuTriggerFor]="bulkStateMenu"
      [matTooltip]="'Sets the state of their ' + selectedContainerIds.length + ' container' + (selectedContainerIds.length === 1 ? '' : 's')">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-sky-400">swap_horiz</mat-icon><span class="hidden sm:inline">Set state</span>
      <mat-icon class="!text-[16px] !h-4 !w-4 opacity-60 -ml-0.5">expand_less</mat-icon>
    </button>
    <mat-menu #bulkStateMenu="matMenu" yPosition="above">
      <div class="px-4 pt-2 pb-1 text-[11px] text-gray-400">Their {{ selectedContainerIds.length }} container{{ selectedContainerIds.length === 1 ? '' : 's' }}</div>
      @for (st of stateOptions; track st.value) {
      <button mat-menu-item (click)="bulkSetState(st.value)">
        <span class="inline-flex items-center gap-2.5"><span class="h-2 w-2 rounded-full" [ngClass]="statusDot(st.value)"></span>{{ st.label }}</span>
      </button>
      }
    </mat-menu>
    @if (bulkBusy) { <mat-spinner diameter="18" class="export-spinner mx-2"></mat-spinner> }
    <button class="ml-1 h-8 w-8 rounded-full text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors" (click)="clearSelection()" matTooltip="Clear selection" aria-label="Clear selection">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
    </button>
  </div>
</div>
}

